import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import ThumbDownOffAltIcon from '@mui/icons-material/ThumbDownOffAlt';
import ThumbUpOffAltIcon from '@mui/icons-material/ThumbUpOffAlt';
import { Button, Chip, Collapse, IconButton, Link, Stack, SvgIconProps, Tooltip, Typography } from '@mui/material';
import React from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { ReadinessCondition, ReadinessRequirement, ReadinessStatus } from '../../api/ach/readiness';
import useCampaignReadiness from '../../hooks/useCampaignReadiness';
import posthog from '../../util/posthog';

// Per-requirement status → status icon + semantic color (matches the app's
// SeverityIcon idiom in Alerts/severity.tsx). UNMET is an actionable failure
// (warning); NOT_EVALUATED could not be computed (neutral/disabled grey, as the
// old 'default' chip was); MET is satisfied (success).
const STATUS_ICON: Record<
  ReadinessStatus,
  { Icon: React.ComponentType<SvgIconProps>; color: SvgIconProps['color'] }
> = {
  MET: { Icon: CheckCircleIcon, color: 'success' },
  UNMET: { Icon: CancelIcon, color: 'warning' },
  NOT_EVALUATED: { Icon: HelpOutlineIcon, color: 'disabled' },
};

// Human-readable label per status — carried by the icon's Tooltip and its
// accessible name (titleAccess) so the text meaning stays discoverable.
const STATUS_LABEL: Record<ReadinessStatus, string> = {
  MET: 'Met',
  UNMET: 'Unmet',
  NOT_EVALUATED: 'Not evaluated',
};

// Condition rows shown before the remainder collapses behind a "Show all (N)" expander.
const MAX_VISIBLE_CONDITIONS = 3;

type FeedbackReason = 'wrong' | 'not_actionable' | 'already_knew';

const FEEDBACK_REASONS: { reason: FeedbackReason; label: string }[] = [
  { reason: 'wrong', label: 'Wrong' },
  { reason: 'not_actionable', label: 'Not actionable' },
  { reason: 'already_knew', label: 'Already knew' },
];

interface ConditionRowProps {
  campaignId: number;
  ruleId: string;
  condition: ReadinessCondition;
}

// ConditionRow renders one structured condition message plus a deep link per entity the
// condition points at. Entities whose server-stamped href is empty (parent-scoped or
// route-less types) are not rendered — the message already names them, so a second copy
// as inert text would only add noise. Links open in a new tab (the AlertRow idiom): the
// panel lives inside AdGroupForm, so navigating in place would abandon in-progress edits.
function ConditionRow({ campaignId, ruleId, condition }: ConditionRowProps) {
  const entities = condition.context?.entities ?? [];

  return (
    <Stack direction="row" spacing={0.75} alignItems="baseline" flexWrap="wrap">
      <Typography variant="caption" color="text.secondary">
        {condition.message}
      </Typography>
      {entities
        .filter((entity) => !!entity.href)
        .map((entity) => (
          <Link
            key={`${entity.type}-${entity.id}`}
            component={RouterLink}
            to={entity.href}
            variant="caption"
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`readiness-entity-link-${entity.type}-${entity.id}`}
            onClick={() =>
              posthog.capture('readiness_link_clicked', {
                campaign_id: campaignId,
                rule_id: ruleId,
                condition_code: condition.code,
                entity_type: entity.type,
              })
            }
          >
            {entity.name || `${entity.type} ${entity.id}`}
          </Link>
        ))}
    </Stack>
  );
}

interface RequirementRowProps {
  campaignId: number;
  requirement: ReadinessRequirement;
}

// RequirementRow renders one actionable requirement: status icon, label, its structured
// conditions (falling back to the flat reason when the requirement carries none), and a
// per-requirement thumbs-up/down control. Feedback is component state only — it is
// reported to PostHog and never persisted, so it survives re-render but not a reload.
function RequirementRow({ campaignId, requirement }: RequirementRowProps) {
  const [expanded, setExpanded] = React.useState(false);
  const [feedback, setFeedback] = React.useState<'none' | 'choosing' | 'sent'>('none');
  const { Icon, color } = STATUS_ICON[requirement.status];
  const label = STATUS_LABEL[requirement.status];
  const conditions = requirement.conditions ?? [];
  const overflow = conditions.length - MAX_VISIBLE_CONDITIONS;

  const sendFeedback = (sentiment: 'up' | 'down', reason?: FeedbackReason) => {
    posthog.capture('validation_feedback', {
      campaign_id: campaignId,
      rule_id: requirement.id,
      sentiment,
      ...(reason ? { reason } : {}),
    });
    setFeedback('sent');
  };

  return (
    <Stack direction="row" spacing={1} alignItems="flex-start" data-testid={`readiness-requirement-${requirement.id}`}>
      <Tooltip title={label} arrow placement="top">
        <Icon color={color} fontSize="small" aria-label={label} titleAccess={label} sx={{ mt: 0.25 }} />
      </Tooltip>
      <Stack spacing={0} flexGrow={1} minWidth={0}>
        <Typography variant="body2">{requirement.label}</Typography>
        {conditions.length > 0 ? (
          <>
            {conditions.slice(0, MAX_VISIBLE_CONDITIONS).map((condition, i) => (
              <ConditionRow
                // Condition codes repeat within a requirement (one per offending entity),
                // so the index disambiguates; the list is server-ordered and static.
                // eslint-disable-next-line react/no-array-index-key
                key={`${condition.code}-${i}`}
                campaignId={campaignId}
                ruleId={requirement.id}
                condition={condition}
              />
            ))}
            {overflow > 0 && (
              <>
                <Collapse in={expanded} unmountOnExit>
                  <Stack spacing={0}>
                    {conditions.slice(MAX_VISIBLE_CONDITIONS).map((condition, i) => (
                      <ConditionRow
                        // eslint-disable-next-line react/no-array-index-key
                        key={`${condition.code}-${MAX_VISIBLE_CONDITIONS + i}`}
                        campaignId={campaignId}
                        ruleId={requirement.id}
                        condition={condition}
                      />
                    ))}
                  </Stack>
                </Collapse>
                <Button
                  variant="text"
                  size="small"
                  onClick={() => setExpanded((v) => !v)}
                  aria-expanded={expanded}
                  data-testid={`readiness-expander-${requirement.id}`}
                  sx={{ alignSelf: 'flex-start', p: 0, minWidth: 0, textTransform: 'none', fontWeight: 400 }}
                >
                  {expanded ? 'Show less' : `Show all (${conditions.length})`}
                </Button>
              </>
            )}
          </>
        ) : (
          requirement.reason && (
            <Typography variant="caption" color="text.secondary">
              {requirement.reason}
            </Typography>
          )
        )}
      </Stack>
      {/* Feedback is about actionable verdicts only — a MET row (verbose view) has nothing to rate. */}
      {requirement.status !== 'MET' && (
        <Stack direction="row" spacing={0.5} alignItems="center" data-testid={`readiness-feedback-${requirement.id}`}>
          {feedback === 'none' && (
            <>
              <Tooltip title="This check was helpful" arrow>
                <IconButton size="small" aria-label="Helpful" onClick={() => sendFeedback('up')}>
                  <ThumbUpOffAltIcon fontSize="inherit" color="disabled" />
                </IconButton>
              </Tooltip>
              <Tooltip title="This check was not helpful" arrow>
                <IconButton size="small" aria-label="Not helpful" onClick={() => setFeedback('choosing')}>
                  <ThumbDownOffAltIcon fontSize="inherit" color="disabled" />
                </IconButton>
              </Tooltip>
            </>
          )}
          {feedback === 'choosing' &&
            FEEDBACK_REASONS.map(({ reason, label: reasonLabel }) => (
              <Chip
                key={reason}
                size="small"
                variant="outlined"
                label={reasonLabel}
                onClick={() => sendFeedback('down', reason)}
              />
            ))}
          {feedback === 'sent' && (
            <Typography variant="caption" color="text.secondary">
              Thanks
            </Typography>
          )}
        </Stack>
      )}
    </Stack>
  );
}

interface CampaignReadinessPanelProps {
  // The Rails campaign id, which in this FE is the AdGroup id.
  campaignId: number;
}

// CampaignReadinessPanel is the Tier-A, read-only publish-readiness surface for an
// AdGroup (ACH-gvkz). It renders a MET/UNMET/NOT_EVALUATED verdict from the ACH
// campaign-readiness endpoint. It NEVER blocks or breaks the AdGroup page: while
// loading, or when ACH is unreachable/errors (the hook degrades to undefined), it
// renders nothing at all.
function CampaignReadinessPanel({ campaignId }: CampaignReadinessPanelProps) {
  const { data, isLoading } = useCampaignReadiness(campaignId);
  // Signature of the last verdict reported to PostHog, so a re-render (or a refetch that
  // returns the same verdict) does not double-count a view.
  const lastViewed = React.useRef<string | null>(null);

  React.useEffect(() => {
    if (!data) {
      return;
    }
    const unmet = data.requirements.filter((req) => req.status === 'UNMET').map((req) => req.id);
    const notEvaluated = data.requirements.filter((req) => req.status === 'NOT_EVALUATED').map((req) => req.id);
    const signature = `${data.ready}|${unmet.join(',')}|${notEvaluated.join(',')}`;
    if (lastViewed.current === signature) {
      return;
    }
    lastViewed.current = signature;
    posthog.capture('readiness_viewed', {
      campaign_id: campaignId,
      ready: data.ready,
      unmet_rule_ids: unmet,
      not_evaluated_rule_ids: notEvaluated,
    });
  }, [campaignId, data]);

  // Quiet fallback: no skeleton, no error — the panel is simply absent until (and
  // unless) ACH returns a verdict. A page without ACH looks exactly as it did before.
  if (isLoading || !data) {
    return null;
  }

  // Default (non-verbose) view: requirements holds only the actionable rows
  // (UNMET + NOT_EVALUATED). When ready, that array is empty.
  const actionable: ReadinessRequirement[] = data.requirements;

  return (
    <Stack spacing={1} px={2} py={1} data-testid="campaign-readiness-panel">
      <Stack direction="row" spacing={1} alignItems="center">
        <Typography variant="subtitle2">Publish readiness</Typography>
        <Chip
          size="small"
          color={data.ready ? 'success' : 'warning'}
          label={data.ready ? 'Ready to publish' : 'Not ready to publish'}
        />
      </Stack>

      {!data.ready && actionable.length > 0 && (
        <Stack spacing={0.5}>
          {actionable.map((req) => (
            <RequirementRow key={req.id} campaignId={campaignId} requirement={req} />
          ))}
        </Stack>
      )}
    </Stack>
  );
}

export default CampaignReadinessPanel;
